import React from 'react';

/**
 * Adaptador local con la firma de `StatusPetra` (@ole/react-library-agents).
 *
 * Hoy renderiza los dos chips Aura del prototipo:
 * - `tone='solid'` (default) → `.pill.pill-*` (32px, con `.dot` opcional). Es el pill del header del board.
 * - `tone='soft'`            → `.badge.b-*` (24px). Son los badges de requisitos / plazos.
 * Migración (OUW-72): reemplazar el `return` por `<StatusPetra label variant tone … />`.
 *
 * Divergencias respecto a `StatusPetraProps` (docs/client-portal/DS-MIGRATION.md):
 * - `dot`: Petra siempre dibuja el dot; el mock solo en los pills del board.
 * - `id` / `className` / `style`: passthrough (`ov-pill`, `act-done` de RequirementRow).
 * - `variant='inactive'` (rojo) y `'info'` no tienen badge Aura; caen en `.b-plazo`.
 */
export type PortalStatusVariant = 'active' | 'inactive' | 'neutral' | 'info' | 'warning';
export type PortalStatusTone = 'solid' | 'soft';

export type PortalStatusProps = {
  label: string;
  variant?: PortalStatusVariant;
  tone?: PortalStatusTone;
  testId?: string;
  // --- locales ---
  dot?: boolean;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
};

const PILL_CLASS: Record<PortalStatusVariant, string> = {
  active: 'pill-success',
  inactive: 'pill-danger',
  neutral: 'pill-neutral',
  info: 'pill-info',
  warning: 'pill-warning',
};

const BADGE_CLASS: Record<PortalStatusVariant, string> = {
  active: 'b-approved',
  inactive: 'b-plazo',
  neutral: 'b-plazo',
  info: 'b-plazo',
  warning: 'b-review',
};

/** Traduce la clase `pill-*` del artifact (OV.pill) a la variante Petra. */
export function pillClassToVariant(pill: string): PortalStatusVariant {
  switch (pill) {
    case 'pill-success':
      return 'active';
    case 'pill-danger':
      return 'inactive';
    case 'pill-neutral':
      return 'neutral';
    case 'pill-info':
      return 'info';
    case 'pill-warning':
    default:
      return 'warning';
  }
}

export function PortalStatus({ label, variant = 'neutral', tone = 'solid', testId, dot, id, className, style }: PortalStatusProps) {
  const base = tone === 'soft' ? `badge ${BADGE_CLASS[variant]}` : `pill ${PILL_CLASS[variant]}`;
  const cls = [base, className || ''].filter(Boolean).join(' ');
  return (
    <span className={cls} id={id} style={style} data-testid={testId}>
      {dot && <span className="dot" />}
      {label}
    </span>
  );
}
